import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, signInAnonymously } from 'firebase/auth';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import firebaseAppletConfig from '../firebase-applet-config.json';

// Silence Firestore reachability errors in logs by converting them to warnings
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  console.error = function (...args: any[]) {
    const isFirestoreReachability = args.some((arg) => {
      if (!arg) return false;
      const str = typeof arg === 'string' ? arg : String(arg.message || arg);
      return str.includes('Could not reach Cloud Firestore backend') ||
             str.includes('operate in offline mode') ||
             str.includes('code=unavailable') ||
             (arg && typeof arg === 'object' && (arg.code === 'unavailable' || arg.name === 'FirebaseError' && (arg.message || '').includes('unavailable')));
    });

    if (isFirestoreReachability) {
      console.warn('[Firestore Offline Notification]', ...args);
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

const firebaseConfig = {
  apiKey: firebaseAppletConfig.apiKey,
  authDomain: firebaseAppletConfig.authDomain,
  projectId: firebaseAppletConfig.projectId,
  storageBucket: firebaseAppletConfig.storageBucket,
  messagingSenderId: firebaseAppletConfig.messagingSenderId,
  appId: firebaseAppletConfig.appId,
};

const app = initializeApp(firebaseConfig);

const dbId = firebaseAppletConfig.firestoreDatabaseId && firebaseAppletConfig.firestoreDatabaseId !== '(default)'
  ? firebaseAppletConfig.firestoreDatabaseId
  : undefined;

// Initialize Firestore with robust multi-tab offline persistence
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
}, dbId);

export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

export const loginWithGoogle = async () => {
  // If user is already logged in, return existing user instantly
  if (auth.currentUser) {
    return auth.currentUser;
  }
  try {
    const result = await signInWithPopup(auth, googleProvider);
    // Cache basic user profile locally for zero-latency UI updates
    if (result.user) {
      try {
        localStorage.setItem('rk_cached_user', JSON.stringify({
          uid: result.user.uid,
          displayName: result.user.displayName,
          email: result.user.email,
          photoURL: result.user.photoURL,
        }));
      } catch {}
    }
    return result.user;
  } catch (error: any) {
    if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
      console.warn('Google Sign-in popup closed/cancelled by user.');
    } else {
      console.error('Error logging in with Google:', error);
    }
    throw error;
  }
};

export const loginAnonymously = async () => {
  try {
    const result = await signInAnonymously(auth);
    return result.user;
  } catch (error) {
    console.error('Error logging in anonymously:', error);
    throw error;
  }
};

export const logout = () => signOut(auth);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errorMsg = error instanceof Error ? error.message : String(error);
  const errorCode = (error as any)?.code;

  // Gracefully handle connectivity or offline status errors instead of crashing
  if (
    errorCode === 'unavailable' || 
    errorMsg.includes('Could not reach Cloud Firestore') || 
    errorMsg.includes('unavailable') || 
    errorMsg.includes('offline')
  ) {
    console.warn(`[Firestore Offline Mode] Operation '${operationType}' on '${path || 'unknown'}' will fall back to local cache:`, errorMsg);
    return;
  }

  const errInfo: FirestoreErrorInfo = {
    error: errorMsg,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
}
